import { useLocalStorage } from "@rehooks/local-storage";
import { useMemo, useRef } from "react";
import { useSearchParams } from "react-router";

import { getSortOptionFromURI } from "components/Filters/helpers";
import {
  EmptystateSearchNoResultsColored,
  EmptystateUsersColored,
} from "components/icons/generated";
import ListContentWrapper from "components/ListContentWrapper";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import SearchInput from "components/SearchInput";
import { getSearchQuery } from "components/SearchInput/helpers";
import TablePagination from "components/Table/Pagination";
import { PAGINATION_OPTIONS } from "components/Table/Pagination/constants";
import usePagination from "components/Table/Pagination/usePagination";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import Table from "ds/components/Table";
import TableBody from "ds/components/Table/Body";
import TableSkeleton from "ds/components/Table/Skeleton";
import TableWrapper from "ds/components/Table/Wrapper";
import TableSimpleHead from "ds/components/TableSimple/Head";
import useErrorHandle from "hooks/useErrorHandle";
import { SearchQueryOrderDirection } from "types/generated";

import {
  SPACE_USERS_FILTERS_ORDER_KEY,
  SPACE_USERS_INITIAL_LIMIT,
  SPACE_USERS_INITIAL_ORDER_BY,
  SPACE_USERS_LIMIT_STORAGE_KEY,
  SPACE_USERS_SORT_CONFIG,
} from "./constants";
import SpaceDetailsLoginPolicyCallout from "../components/LoginPolicyCallout";
import SpaceDetailsUsersRows from "./Rows";
import useSpaceDetailsUsers from "./useSpaceDetailsUsers";
import useSpaceDetailsUsersTableStore from "./useStore";

type SpaceDetailsUsersProps = {
  spaceId: string;
};

const TABLE_ARIA_LABEL = "Space users table";

const SpaceDetailsUsers = ({ spaceId }: SpaceDetailsUsersProps) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const [storedRowsPerPage] = useLocalStorage<number>(
    SPACE_USERS_LIMIT_STORAGE_KEY,
    SPACE_USERS_INITIAL_LIMIT
  );

  const first = storedRowsPerPage || SPACE_USERS_INITIAL_LIMIT;

  const fullTextSearch = getSearchQuery(searchParams) || null;
  const orderBy = useMemo(
    () =>
      getSortOptionFromURI(
        searchParams,
        SPACE_USERS_INITIAL_ORDER_BY.field,
        SearchQueryOrderDirection.Asc
      )[0] ?? SPACE_USERS_INITIAL_ORDER_BY,
    [searchParams]
  );

  const columnIds = useSpaceDetailsUsersTableStore((state) => state.columnsOrder.visible);

  const {
    loadUsers,
    error,
    users,
    initialLoading,
    loadingContent,
    totalCount,
    endCursor,
    startCursor,
    paginationResetKey,
  } = useSpaceDetailsUsers({ spaceId, orderBy, first, fullTextSearch });

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: loadUsers,
    endCursor,
    startCursor,
    allItemsCount: totalCount,
    visibleItemsCount: users.length,
    initialLimit: SPACE_USERS_INITIAL_LIMIT,
    storageKey: SPACE_USERS_LIMIT_STORAGE_KEY,
    resetKey: paginationResetKey,
    loading: loadingContent,
  });

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading) {
    return <PageLayoutSkeleton />;
  }

  return (
    <>
      <PageInfoHeaderWrapper>
        <PageInfoHeaderTitle title="Users" />
        <PageInfoHeaderActions>
          <SearchInput
            placeholder="Search users"
            filtersOrderSettingsKey={SPACE_USERS_FILTERS_ORDER_KEY}
          />
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>

      <SpaceDetailsLoginPolicyCallout />

      <ListContentWrapper>
        {!users.length && !fullTextSearch && (
          <EmptyState
            title="This space has no users yet"
            icon={EmptystateUsersColored}
            caption="Users with access to this space will show up here."
          />
        )}

        {!users.length && !!fullTextSearch && (
          <EmptyState
            title="No users found"
            icon={EmptystateSearchNoResultsColored}
            caption="No users match your search."
          />
        )}

        {!!users.length && (
          <>
            <TableWrapper ref={tableRef} hasPagination>
              <Table ariaLabel={TABLE_ARIA_LABEL}>
                <TableSimpleHead
                  store={useSpaceDetailsUsersTableStore}
                  ariaLabel={TABLE_ARIA_LABEL}
                  tableRef={tableRef}
                  sortConfig={SPACE_USERS_SORT_CONFIG}
                  filtersStorageKey={SPACE_USERS_FILTERS_ORDER_KEY}
                  hideSettings
                  stickyRight={() => false}
                />
                {loadingContent && (
                  <TableSkeleton columnIds={columnIds} rowsPerPage={rowsPerPage} />
                )}
                {!loadingContent && (
                  <TableBody>
                    <SpaceDetailsUsersRows
                      data={users}
                      store={useSpaceDetailsUsersTableStore}
                      tableRef={tableRef}
                    />
                  </TableBody>
                )}
              </Table>
            </TableWrapper>
            <TablePagination
              from={from}
              to={to}
              loading={loadingContent}
              goToNextPage={goToNextPage}
              setRowsPerPage={setRowsPerPage}
              goToPrevPage={goToPrevPage}
              goToFirstPage={goToFirstPage}
              goToLastPage={goToLastPage}
              rowsPerPage={rowsPerPage}
              itemsCount={totalCount}
              lastPage={lastPage}
              page={page}
              paginationOptions={PAGINATION_OPTIONS}
            />
          </>
        )}
      </ListContentWrapper>
    </>
  );
};

export default SpaceDetailsUsers;
